import type {
  TertiaryNavigationActionControlConfig,
  TertiaryNavigationControlConfig,
  TertiaryNavigationLeftActionControlConfig,
  TertiaryNavigationLinkItem,
  TertiaryNavigationState,
} from './tertiaryNavigation.types';

interface GetTertiaryNavigationActiveValueOptions {
  currentPathname: string;
  currentSearch: string;
  items?: TertiaryNavigationLinkItem[];
  value?: TertiaryNavigationState['value'];
}

const getPathnameFromNavigationTarget = (to: string) => to.split('?')[0] || to;

export const getTertiaryNavigationActiveValue = ({
  currentPathname,
  currentSearch,
  items = [],
  value,
}: GetTertiaryNavigationActiveValueOptions) => {
  // Controlled values come from query-backed tabs such as ?tab=<value>; route-backed tabs use URL matching.
  if (value !== undefined) {
    return value;
  }

  const currentUrl = `${currentPathname}${currentSearch}`;

  // Exact URL matching supports route-backed tertiary tabs that share a pathname but differ by query params.
  const exactItem = items.find(item => item.to && item.to === currentUrl);
  if (exactItem) {
    return exactItem.value;
  }

  const pathnameItems = items.filter(
    item => item.to && getPathnameFromNavigationTarget(item.to) === currentPathname
  );
  const basePathnameItem = pathnameItems.find(item => item.to && !item.to.includes('?'));

  return (basePathnameItem ?? pathnameItems[0])?.value ?? false;
};

const areTertiaryNavigationControlConfigsEqual = (
  previousControl: TertiaryNavigationControlConfig,
  nextControl: TertiaryNavigationControlConfig
) => {
  if (
    previousControl.type !== nextControl.type
    || !Object.is(previousControl.isVisible, nextControl.isVisible)
    || !Object.is(previousControl.key, nextControl.key)
    || !Object.is(previousControl.testId, nextControl.testId)
  ) {
    return false;
  }

  switch (previousControl.type) {
    case 'addButton':
      return nextControl.type === 'addButton'
        && Object.is(previousControl.disabled, nextControl.disabled)
        && Object.is(previousControl.hasPopup, nextControl.hasPopup)
        && Object.is(previousControl.onClick, nextControl.onClick);
    case 'aiButton':
      return nextControl.type === 'aiButton'
        && Object.is(previousControl.ariaLabel, nextControl.ariaLabel)
        && Object.is(previousControl.disabled, nextControl.disabled)
        && Object.is(previousControl.hasPopup, nextControl.hasPopup)
        && Object.is(previousControl.icon, nextControl.icon)
        && Object.is(previousControl.onClick, nextControl.onClick);
    case 'aiPrompt':
      return nextControl.type === 'aiPrompt'
        && Object.is(previousControl.ariaLabel, nextControl.ariaLabel)
        && Object.is(previousControl.busyHint, nextControl.busyHint)
        && Object.is(previousControl.collapsedTooltipTitle, nextControl.collapsedTooltipTitle)
        && Object.is(previousControl.disabled, nextControl.disabled)
        && Object.is(previousControl.icon, nextControl.icon)
        && Object.is(previousControl.isBusy, nextControl.isBusy)
        && Object.is(previousControl.maxRows, nextControl.maxRows)
        && Object.is(previousControl.onOpenChange, nextControl.onOpenChange)
        && Object.is(previousControl.onSubmit, nextControl.onSubmit)
        && Object.is(previousControl.panelAriaLabel, nextControl.panelAriaLabel)
        && Object.is(previousControl.panelIcon, nextControl.panelIcon)
        && Object.is(previousControl.placeholder, nextControl.placeholder)
        && Object.is(previousControl.submitAriaLabel, nextControl.submitAriaLabel)
        && Object.is(previousControl.submitHint, nextControl.submitHint);
    case 'button':
      return nextControl.type === 'button'
        && Object.is(previousControl.disabled, nextControl.disabled)
        && Object.is(previousControl.endIcon, nextControl.endIcon)
        && Object.is(previousControl.hasPopup, nextControl.hasPopup)
        && Object.is(previousControl.errorLabel, nextControl.errorLabel)
        && Object.is(previousControl.isSaving, nextControl.isSaving)
        && Object.is(previousControl.label, nextControl.label)
        && Object.is(previousControl.locked, nextControl.locked)
        && Object.is(previousControl.onClick, nextControl.onClick)
        && Object.is(previousControl.saveFailed, nextControl.saveFailed)
        && Object.is(previousControl.savedLabel, nextControl.savedLabel)
        && Object.is(previousControl.savingLabel, nextControl.savingLabel)
        && Object.is(previousControl.startIcon, nextControl.startIcon)
        && Object.is(previousControl.variant, nextControl.variant);
    case 'checkbox':
      return nextControl.type === 'checkbox'
        && Object.is(previousControl.checked, nextControl.checked)
        && Object.is(previousControl.disabled, nextControl.disabled)
        && Object.is(previousControl.indeterminate, nextControl.indeterminate)
        && Object.is(previousControl.inputAriaLabel, nextControl.inputAriaLabel)
        && Object.is(previousControl.label, nextControl.label)
        && Object.is(previousControl.onChange, nextControl.onChange);
    case 'currencySelector':
      return nextControl.type === 'currencySelector'
        && Object.is(previousControl.currency, nextControl.currency)
        && Object.is(previousControl.currencyAriaLabel, nextControl.currencyAriaLabel)
        && Object.is(previousControl.onCurrencyClick, nextControl.onCurrencyClick)
        && Object.is(previousControl.onUnitClick, nextControl.onUnitClick)
        && Object.is(previousControl.unit, nextControl.unit)
        && Object.is(previousControl.unitAriaLabel, nextControl.unitAriaLabel);
    case 'iconButton':
      return nextControl.type === 'iconButton'
        && Object.is(previousControl.ariaLabel, nextControl.ariaLabel)
        && Object.is(previousControl.disabled, nextControl.disabled)
        && Object.is(previousControl.hasPopup, nextControl.hasPopup)
        && Object.is(previousControl.icon, nextControl.icon)
        && Object.is(previousControl.onClick, nextControl.onClick)
        && Object.is(previousControl.variant, nextControl.variant);
    case 'splitButton':
      return nextControl.type === 'splitButton'
        && Object.is(previousControl.disabled, nextControl.disabled)
        && Object.is(previousControl.errorLabel, nextControl.errorLabel)
        && Object.is(previousControl.hasPopup, nextControl.hasPopup)
        && Object.is(previousControl.isSaving, nextControl.isSaving)
        && Object.is(previousControl.label, nextControl.label)
        && Object.is(previousControl.locked, nextControl.locked)
        && Object.is(previousControl.mainDisabled, nextControl.mainDisabled)
        && Object.is(previousControl.menuAriaLabel, nextControl.menuAriaLabel)
        && Object.is(previousControl.menuDisabled, nextControl.menuDisabled)
        && Object.is(previousControl.onClick, nextControl.onClick)
        && Object.is(previousControl.onMenuClick, nextControl.onMenuClick)
        && Object.is(previousControl.saveFailed, nextControl.saveFailed)
        && Object.is(previousControl.savedLabel, nextControl.savedLabel)
        && Object.is(previousControl.savingLabel, nextControl.savingLabel)
        && Object.is(previousControl.variant, nextControl.variant);
    default:
      return false;
  }
};

const areTertiaryNavigationControlListsEqual = (
  previousControls: TertiaryNavigationActionControlConfig[] | TertiaryNavigationLeftActionControlConfig[] = [],
  nextControls: TertiaryNavigationActionControlConfig[] | TertiaryNavigationLeftActionControlConfig[] = []
) => {
  if (previousControls.length !== nextControls.length) {
    return false;
  }

  return previousControls.every((previousControl, index) => {
    const nextControl = nextControls[index];

    return Boolean(nextControl) && areTertiaryNavigationControlConfigsEqual(previousControl, nextControl);
  });
};

const areTertiaryNavigationSkeletonPropsEqual = (
  previousProps?: TertiaryNavigationState['skeletonProps'],
  nextProps?: TertiaryNavigationState['skeletonProps']
) => {
  if (!previousProps || !nextProps) {
    return previousProps === nextProps;
  }

  return Object.is(previousProps.actionCount, nextProps.actionCount)
    && Object.is(previousProps.itemCount, nextProps.itemCount)
    && Object.is(previousProps.showActions, nextProps.showActions)
    && Object.is(previousProps.showLeftAction, nextProps.showLeftAction);
};

export const areTertiaryNavigationStatesEqual = (
  previousState: TertiaryNavigationState | null,
  nextState: TertiaryNavigationState | null
) => {
  if (previousState === nextState) {
    return true;
  }

  if (!previousState || !nextState) {
    return false;
  }

  if (
    !areTertiaryNavigationControlListsEqual(previousState.actions, nextState.actions) ||
    !Object.is(previousState.actionsNode, nextState.actionsNode) ||
    !Object.is(previousState.ariaLabel, nextState.ariaLabel) ||
    !Object.is(previousState.isLoading, nextState.isLoading) ||
    !areTertiaryNavigationControlListsEqual(previousState.leftActions, nextState.leftActions) ||
    !Object.is(previousState.leftActionsNode, nextState.leftActionsNode) ||
    !Object.is(previousState.onItemMenuClick, nextState.onItemMenuClick) ||
    !areTertiaryNavigationSkeletonPropsEqual(previousState.skeletonProps, nextState.skeletonProps) ||
    !Object.is(previousState.value, nextState.value)
  ) {
    return false;
  }

  const previousItems = previousState.items ?? [];
  const nextItems = nextState.items ?? [];

  if (previousItems.length !== nextItems.length) {
    return false;
  }

  return previousItems.every((previousItem, index) => {
    const nextItem = nextItems[index];

    if (!nextItem) {
      return false;
    }

    return Object.is(previousItem.disabled, nextItem.disabled)
      && Object.is(previousItem.hasError, nextItem.hasError)
      && Object.is(previousItem.label, nextItem.label)
      && Object.is(previousItem.showMenu, nextItem.showMenu)
      && Object.is(previousItem.to, nextItem.to)
      && Object.is(previousItem.value, nextItem.value);
  });
};
